import { useState, useEffect } from "react";
import { Check, X, Eye, Trash2, Phone, Calendar, Clock, FileText, Image, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  contacted: "bg-blue-100 text-blue-800",
  scheduled: "bg-purple-100 text-purple-800",
  completed: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const statusLabels = {
  pending: "Pendiente",
  contacted: "Contactado",
  scheduled: "Agendado",
  completed: "Completado",
  rejected: "Rechazado",
};

export default function AdminEmprendedores() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [notes, setNotes] = useState("");
  const [mediaCache, setMediaCache] = useState({});

  useEffect(() => {
    fetchRequests();
  }, [statusFilter, page, limit]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("skip", String((page - 1) * limit));
      params.append("limit", String(limit));

      const response = await axios.get(`${API}/admin/emprendedores?${params}`, {
      });
      setRequests(response.data.items);
      setTotal(response.data.total);
      
      // Pre-fetch media info for requests with photos/videos
      const mediaIds = [];
      response.data.items.forEach(req => {
        if (req.photo_ids) mediaIds.push(...req.photo_ids);
        if (req.video_ids) mediaIds.push(...req.video_ids);
      });
      
      if (mediaIds.length > 0) {
        const uniqueIds = [...new Set(mediaIds)];
        const newCache = { ...mediaCache };
        for (const id of uniqueIds) {
          if (!newCache[id]) {
            try {
              const mediaRes = await axios.get(`${API}/media/${id}`);
              newCache[id] = mediaRes.data;
            } catch (e) {
              console.error(`Failed to fetch media ${id}:`, e);
            }
          }
        }
        setMediaCache(newCache);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("Error al cargar las solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus, newNotes = null) => {
    try {
      const data = { status: newStatus };
      if (newNotes !== null) data.notes = newNotes;
      
      await axios.put(`${API}/admin/emprendedores/${requestId}`, data, {
      });
      toast.success(`Solicitud ${statusLabels[newStatus].toLowerCase()}`);
      fetchRequests();
      setSelectedRequest(null);
      setNotes("");
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const deleteRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/admin/emprendedores/${requestId}`, {
      });
      toast.success("Solicitud eliminada");
      fetchRequests();
      setDeleteDialog(null);
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (url.startsWith('/uploads/')) {
      return `${backendUrl}/api${url}`;
    }
    return `${backendUrl}${url}`;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-emprendedores">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Solicitudes de Emprendedores
            </h1>
            <p className="text-brand-textSecondary mt-1">
              {pendingCount > 0 ? (
                <span className="text-yellow-600 font-medium">
                  {pendingCount} solicitudes pendientes de revisión
                </span>
              ) : (
                `${total} solicitudes en total`
              )}
            </p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="contacted">Contactadas</SelectItem>
              <SelectItem value="scheduled">Agendadas</SelectItem>
              <SelectItem value="completed">Completadas</SelectItem>
              <SelectItem value="rejected">Rechazadas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-yellow-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">
              {requests.filter((r) => r.status === "pending").length}
            </p>
            <p className="text-sm text-yellow-700">Pendientes</p>
          </div>
          <div className="bg-blue-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {requests.filter((r) => r.status === "contacted").length}
            </p>
            <p className="text-sm text-blue-700">Contactados</p>
          </div>
          <div className="bg-purple-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">
              {requests.filter((r) => r.status === "scheduled").length}
            </p>
            <p className="text-sm text-purple-700">Agendados</p>
          </div>
          <div className="bg-green-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {requests.filter((r) => r.status === "completed").length}
            </p>
            <p className="text-sm text-green-700">Completados</p>
          </div>
          <div className="bg-red-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-red-600">
              {requests.filter((r) => r.status === "rejected").length}
            </p>
            <p className="text-sm text-red-700">Rechazados</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Emprendimiento</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Día de Visita</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : requests.length > 0 ? (
                requests.map((req) => (
                  <TableRow key={req.request_id} className={req.status === "pending" ? "bg-yellow-50/50" : ""}>
                    <TableCell>
                      <div className="font-medium text-brand-text line-clamp-1">
                        {req.name}
                      </div>
                      <p className="text-xs text-gray-400 line-clamp-1">{req.description}</p>
                    </TableCell>
                    <TableCell>
                      <a href={`tel:${req.phone}`} className="flex items-center gap-1 text-primary hover:underline">
                        <Phone className="h-3 w-3" />
                        {req.phone}
                      </a>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm text-brand-textSecondary">
                        <Calendar className="h-4 w-4" />
                        {req.visit_date}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[req.status] || statusColors.pending}>
                        {statusLabels[req.status] || "Pendiente"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-brand-textSecondary text-sm">
                      {req.created_at
                        ? new Date(req.created_at).toLocaleDateString("es-HN")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedRequest(req);
                            setNotes(req.notes || "");
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeleteDialog(req)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <p className="text-brand-textSecondary">No hay solicitudes</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Detail Dialog */}
        <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detalle de Solicitud - Emprendedor</DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4 mt-4">
                <div>
                  <h3 className="font-heading text-xl font-bold text-brand-text">
                    {selectedRequest.name}
                  </h3>
                  <Badge className={`mt-2 ${statusColors[selectedRequest.status] || statusColors.pending}`}>
                    {statusLabels[selectedRequest.status] || "Pendiente"}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Teléfono</p>
                    <a href={`tel:${selectedRequest.phone}`} className="font-medium text-primary hover:underline">
                      {selectedRequest.phone}
                    </a>
                  </div>
                  <div>
                    <p className="text-gray-500">Día de Visita Solicitado</p>
                    <p className="font-medium">{selectedRequest.visit_date}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Fecha de Solicitud</p>
                    <p className="font-medium">
                      {selectedRequest.created_at
                        ? new Date(selectedRequest.created_at).toLocaleString("es-HN")
                        : "-"}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-gray-500 text-sm mb-2">Descripción del Emprendimiento</p>
                  <p className="text-brand-text bg-gray-50 p-4 rounded-xl whitespace-pre-wrap">
                    {selectedRequest.description}
                  </p>
                </div>

                {/* Photos Section */}
                {selectedRequest.photo_ids && selectedRequest.photo_ids.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-sm mb-2 flex items-center gap-2">
                      <Image className="h-4 w-4" />
                      Fotos ({selectedRequest.photo_ids.length})
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {selectedRequest.photo_ids.map((photoId) => {
                        const media = mediaCache[photoId];
                        return media ? (
                          <a
                            key={photoId}
                            href={getImageUrl(media.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-80 transition-opacity"
                          >
                            <img
                              src={getImageUrl(media.url)}
                              alt="Foto"
                              className="w-full h-full object-cover"
                            />
                          </a>
                        ) : (
                          <div key={photoId} className="aspect-square rounded-lg bg-gray-100 animate-pulse" />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Videos Section */}
                {selectedRequest.video_ids && selectedRequest.video_ids.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-sm mb-2 flex items-center gap-2">
                      <Video className="h-4 w-4" />
                      Videos ({selectedRequest.video_ids.length})
                    </p>
                    <div className="space-y-2">
                      {selectedRequest.video_ids.map((videoId) => {
                        const media = mediaCache[videoId];
                        return media ? (
                          <div key={videoId} className="rounded-lg overflow-hidden bg-gray-900">
                            <video
                              src={getImageUrl(media.url)}
                              controls
                              className="w-full max-h-48"
                            />
                          </div>
                        ) : (
                          <div key={videoId} className="h-24 rounded-lg bg-gray-100 animate-pulse" />
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-gray-500 text-sm mb-2">Notas internas</p>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Agregar notas sobre esta solicitud..."
                    className="min-h-24"
                  />
                </div>

                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedRequest.request_id, "contacted", notes)}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <Phone className="h-4 w-4 mr-1" />
                    Marcar Contactado
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedRequest.request_id, "scheduled", notes)}
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  >
                    <Calendar className="h-4 w-4 mr-1" />
                    Marcar Agendado
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedRequest.request_id, "completed", notes)}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Marcar Completado
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedRequest.request_id, "rejected", notes)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Rechazar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Eliminar Solicitud</DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas eliminar la solicitud de "{deleteDialog?.name}"? Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog(null)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={() => deleteRequest(deleteDialog?.request_id)}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="emprendedores-pagination"
          label="solicitudes"
        />
    </AdminLayout>
  );
}
